import http from 'services/http';
import type { PortalPlan } from './offer';
import type { OnlinePaymentMethod, PaymentMethod } from 'modules/payments/methods';
import type { PortalCycle } from './cycles';

interface PortalCheckout {
  checkoutUrl: string;
  orderId: string;
}

const PAYME_ORIGINS = ['https://checkout.paycom.uz', 'https://checkout.test.paycom.uz', 'https://test.paycom.uz'];

/** The browser only leaves for a provider's own page: Payme, or ATMOS (Uzcard/Humo) on atmos.uz. */
function assertProviderCheckout(data: PortalCheckout): PortalCheckout {
  const url = new URL(data.checkoutUrl);
  const isAtmos = url.protocol === 'https:' && (url.hostname === 'atmos.uz' || url.hostname.endsWith('.atmos.uz'));
  if ((!PAYME_ORIGINS.includes(url.origin) && !isAtmos) || url.username || url.password) {
    throw new Error('Invalid checkout URL');
  }
  return data;
}

export async function createPortalCheckout(
  planKey: string, expectedAmount: number, method: OnlinePaymentMethod, signal: AbortSignal, cycle: PortalCycle = 'monthly',
) {
  // A month is the default, so a monthly checkout sends no cycle (an older API refuses unknown fields).
  const body = cycle === 'monthly' ? { planKey, expectedAmount, method } : { planKey, expectedAmount, method, cycle };
  const { data } = await http.request.post<PortalCheckout>('/api/portal/billing/checkout', body, { signal });
  return assertProviderCheckout(data);
}

/** Online top-up of what the balance is short for a paid test. The server re-derives the amount. */
export async function createPortalTopUpCheckout(testId: string, expectedAmount: number, method: OnlinePaymentMethod, signal: AbortSignal) {
  const { data } = await http.request.post<PortalCheckout>(
    '/api/portal/billing/top-up', { testId, expectedAmount, method }, { signal },
  );
  return assertProviderCheckout(data);
}

/** The payment methods a learner can use now: the same admin switches as organizations, in display order. */
export async function getPortalPaymentMethods(signal: AbortSignal): Promise<PaymentMethod[]> {
  const { data } = await http.request.get<{ methods: PaymentMethod[] }>('/api/portal/billing/methods', { signal });
  return Array.isArray(data?.methods) ? data.methods : [];
}

export interface PortalSubscription {
  planKey?: string;
  planName: string;
  /** Absent from an older API, which only sold months. */
  billingCycle?: PortalCycle;
  periodStart?: string;
  periodEnd: string;
  mockLimit: number;
  mocksRemaining: number;
  /** IELTS/SAT attempts whose full analysis the plan unlocks per period; absent from an older API. */
  analysisLimit?: number;
  analysesRemaining?: number;
  /** The running plan includes vocabulary definitions and the download. */
  hasVocabulary?: boolean;
  isActive?: boolean;
  isExpired?: boolean;
  isRenewalDue?: boolean;
  daysRemaining?: number;
}

export interface PortalPaymentStatus {
  kind: 'portal_subscription' | 'portal_balance_topup';
  state: 'pending' | 'created' | 'paid' | 'cancelled' | 'refunded';
  activated: boolean;
  planName?: string;
  periodStart?: string;
  periodEnd?: string;
  /** Top-ups only: the credited amount and, once credited, the learner's balance. */
  amount?: number;
  balanceSom?: number;
}

export async function getPortalSubscription(signal: AbortSignal): Promise<PortalSubscription | null> {
  const { data } = await http.request.get<PortalSubscription | null>('/api/portal/billing/subscription', { signal });
  return data;
}

export async function getPortalPaymentStatus(orderId: string, signal: AbortSignal): Promise<PortalPaymentStatus> {
  const { data } = await http.request.get<PortalPaymentStatus>(`/api/portal/billing/orders/${encodeURIComponent(orderId)}`, { signal });
  return data;
}

export interface PortalPaymentDetails {
  cardNumber: string;
  cardHolderName: string;
}

// Keep the recipient allowlist aligned with the API and admin form. Constructing
// the Unicode regex here also works with this app's ES5 TypeScript target.
const CARD_HOLDER_NAME_PATTERN = new RegExp(String.raw`^(?=.*\p{L})(?!.*\p{Default_Ignorable_Code_Point})[\p{L}\p{M} .'\u2018\u2019-]{2,100}(?![\s\S])`, 'u');

export async function getPortalPaymentDetails(signal: AbortSignal): Promise<PortalPaymentDetails | null> {
  const response = await http.request.get<{ data: PortalPaymentDetails | null }>(
    '/api/portal/plans/payment-details',
    { signal, headers: { 'Cache-Control': 'no-cache' } },
  );
  const details = response.data.data;
  if (details === null) return null;
  if (!details || typeof details.cardNumber !== 'string' || details.cardNumber.length !== 16 || !/^\d{16}$/.test(details.cardNumber) ||
    typeof details.cardHolderName !== 'string' || details.cardHolderName.trim().length < 2 ||
    details.cardHolderName.length > 100 || !CARD_HOLDER_NAME_PATTERN.test(details.cardHolderName)) {
    throw new Error('Payment details are unavailable');
  }
  return { cardNumber: details.cardNumber, cardHolderName: details.cardHolderName };
}

/** The public plan catalogue. The checkout re-derives every price on the server; these are for display. */
export async function getPortalPlans(signal: AbortSignal): Promise<PortalPlan[]> {
  const { data } = await http.request.get<{ data: PortalPlan[] }>('/api/portal/plans', { signal });
  return Array.isArray(data?.data) ? data.data : [];
}

/** The welcome bonus a learner finishing onboarding now would get; 0 when it is off or has ended. */
export interface WelcomeBonusOffer {
  balanceSom: number;
  endsAt: string | null;
}

export async function getWelcomeBonus(signal?: AbortSignal) {
  const { data } = await http.request.get<WelcomeBonusOffer>('/api/public/tests/default-balance', { signal });
  return data;
}
